import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { MultiPartInput } from 'Views/Components/New/CompositeInputs';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextInput } from 'Views/Components/New/TextInput';
import {
	UserVehicleDto,
	UpdateVehicleData,
	CreateVehicleData,
	updateUserVehicle,
	createUserVehicle,
	getCargoTypes,
	CargoTypeForVehicleDto,
} from 'Services/Api/_HumanWritten/UserVehicleService';
import { appConfigStore } from 'Models/AppConfigStore';
import {
	filterMeasurementsForContext,
	findAllMeasurementsForFerryTrip,
	getMeasurementLabelFromId,
	getMeasurementValueFromId,
	resolveAllowedMeasurementId,
} from 'Util/_HumanWritten/MeasurementUtils';
import { MeasurementFilterContext } from 'Util/_HumanWritten/MeasurementRestrictions';
import { getEffectiveCharacteristicIds } from 'Modules/Cargo';
import { useSubjectUserDisplayNameId } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/BookingDetails/useSubjectUserDisplayNameId';
import { convertWeight, formatWeight, parseWeightUnit, weightUnitLabel } from 'Util/Weight';
import alertToast from 'Util/ToastifyUtils';
import { store } from 'Models/Store';
import { getLowerCaseMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

const WEIGHT_UNITS = ['kg', 't', 'lb', 'ton_us'] as const;

interface EditFormData {
	cargoIdentification: string;
	cargoMake: string;
	cargoModel: string;
	cargoTypeId: string;
	selectedLengthId: string;
	selectedWeightId: string;
	selectedWidthId: string | null;
	exactWeight: number | null;
	exactWeightUnit: string | null;
}

interface VehicleEditModalContentProps {
	vehicle?: UserVehicleDto;
	userId?: string;
	onConfirm: () => void;
	onCancel: () => void;
	onSuccess: () => void;
}

function VehicleEditModalContent({ vehicle, userId, onConfirm, onCancel, onSuccess }: VehicleEditModalContentProps) {
	const isCreate = !vehicle;
	const [isSaving, setIsSaving] = useState(false);
	const [cargoTypes, setCargoTypes] = useState<CargoTypeForVehicleDto[]>([]);
	const [formData, setFormData] = useState<EditFormData>({
		cargoIdentification: vehicle?.cargoIdentification ?? '',
		cargoMake: vehicle?.cargoType?.cargoMake ?? '',
		cargoModel: vehicle?.cargoType?.cargoModel ?? '',
		cargoTypeId: vehicle?.cargoType?.id ?? '',
		selectedLengthId: vehicle?.selectedLengthId ?? '',
		selectedWeightId: vehicle?.selectedWeightId ?? '',
		selectedWidthId: vehicle?.selectedWidthId ?? null,
		exactWeight: vehicle?.exactWeight ?? null,
		exactWeightUnit: vehicle?.exactWeightUnit ?? appConfigStore.weightDisplayUnit,
	});
	const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
	const [lengthWarning, setLengthWarning] = useState<string | null>(null);
	const subjectUserDisplayNameId = useSubjectUserDisplayNameId(userId);

	useEffect(() => {
		getCargoTypes().then(types => {
			setCargoTypes(types);
		});
	}, []);

	const makeOptions = _.uniq(
		cargoTypes.sort((a, b) => a.cargoMake.trim().localeCompare(b.cargoMake.trim())).map(x => x.cargoMake.trim()),
	)
		.filter(x => !x.startsWith('Generic'))
		.map(x => ({ value: x, label: x }));

	const modelOptions = cargoTypes
		.filter(x => x.cargoMake.trim() === formData.cargoMake)
		.sort((a, b) => a.cargoModel.localeCompare(b.cargoModel))
		.map(x => ({ value: x.cargoModel, label: x.cargoModel }));

	const selectedCargoType = cargoTypes.find(
		x => x.cargoMake.trim() === formData.cargoMake && x.cargoModel === formData.cargoModel,
	);
	const minimumLengthId = selectedCargoType?.minimumLengthId;
	const minLengthValue = getMeasurementValueFromId(minimumLengthId);

	// A saved vehicle has no driver and no trip, so the driver-passenger-type dimension does not apply
	// here (left undefined, which skips it) — only the vehicle's own characteristics and the account
	// holder's customer type do.
	//
	// Taken as a function of the vehicle type rather than of `selectedCargoType`, because the
	// make/model handler has to resolve the same context for a type it has only just picked, before
	// the state carrying it has been written. Both callers must agree: the picker and the pre-fill
	// disagreeing is what put an unavailable length in the field in the first place.
	const buildFilterContext = (cargoTypeId: string | undefined): MeasurementFilterContext => ({
		cargoCharacteristicIds: getEffectiveCharacteristicIds(
			store.cargoTypeCharacteristicsMap?.[cargoTypeId ?? ''] ?? [],
			undefined,
			undefined,
		),
		userDisplayNameId: subjectUserDisplayNameId,
	});

	// The saved length is always kept in the list so a vehicle recorded before a rule existed still
	// shows its own value.
	const visibleLengths = filterMeasurementsForContext(
		findAllMeasurementsForFerryTrip('LENGTH', 'vehicle'),
		buildFilterContext(selectedCargoType?.id),
		[formData.selectedLengthId],
	);

	const lengthOptions =
		visibleLengths?.map(x => {
			const isTooSmall = minimumLengthId && getMeasurementValueFromId(x.id) < minLengthValue;
			return {
				label: isTooSmall ? `${x.label} - vehicle too large` : x.label,
				value: x.id,
				isDisabled: !!isTooSmall,
			};
		}) ?? [];

	// Read-only weight: the customer's exact weight (in the configured display
	// unit) when set, otherwise the selected weight band label. Only shown when
	// an exact-weight feature flag is enabled.
	const showWeight = appConfigStore.vehicleExactWeightInputEnabled || appConfigStore.trailerExactWeightInputEnabled;
	const displayUnit = appConfigStore.weightDisplayUnit;
	const weightDisplay =
		vehicle?.exactWeight != null && vehicle.exactWeightUnit
			? formatWeight(
					Number(vehicle.exactWeight),
					parseWeightUnit(vehicle.exactWeightUnit, displayUnit),
					displayUnit,
				)
			: getMeasurementLabelFromId(formData.selectedWeightId);

	const weightUnitOptions = WEIGHT_UNITS.map(u => ({ value: u, label: weightUnitLabel(u) }));
	const exactWeightUnitOption = weightUnitOptions.find(o => o.value === formData.exactWeightUnit) ?? null;

	// When adding a vehicle, the exact weight is editable if the chosen vehicle
	// type requires it: master switch on AND (no required characteristics → all
	// vehicles, or the type carries a matching characteristic).
	const requiresExactWeight =
		isCreate && appConfigStore.cargoTypeRequiresExactWeight(selectedCargoType?.characteristicIds ?? []);

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<string, string>> = {};

		if (!formData.cargoIdentification.trim()) {
			newErrors.cargoIdentification = 'Rego is required';
		}

		if (!formData.cargoMake) {
			newErrors.cargoMake = 'Make is required';
		}

		if (!formData.cargoModel) {
			newErrors.cargoModel = 'Model is required';
		}

		if (isCreate) {
			if (!formData.selectedLengthId) {
				newErrors.selectedLengthId = 'Length is required';
			}
			if (!formData.selectedWeightId) {
				newErrors.selectedWeightId = 'Weight is required';
			}
			if (requiresExactWeight && (formData.exactWeight == null || formData.exactWeight <= 0)) {
				newErrors.exactWeight = 'Exact weight is required';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const resolveCargoTypeId = (): string => {
		const match = cargoTypes.find(
			x => x.cargoMake.trim() === formData.cargoMake && x.cargoModel === formData.cargoModel,
		);
		return match?.id ?? formData.cargoTypeId;
	};

	const handleSave = async () => {
		if (!validateForm()) {
			return;
		}

		setIsSaving(true);
		try {
			const cargoTypeId = resolveCargoTypeId();

			if (isCreate) {
				const sendExactWeight = requiresExactWeight && formData.exactWeight != null;
				const createData: CreateVehicleData = {
					cargoIdentification: formData.cargoIdentification.trim(),
					cargoTypeId,
					selectedLengthId: formData.selectedLengthId,
					selectedWeightId: formData.selectedWeightId,
					selectedWidthId: formData.selectedWidthId,
					// Server re-derives the matching weight band from the exact weight.
					exactWeight: sendExactWeight ? formData.exactWeight : null,
					exactWeightUnit: sendExactWeight ? formData.exactWeightUnit : null,
				};
				await createUserVehicle(userId!, createData);
				alertToast('Vehicle added successfully', 'success', 'Vehicle added');
			} else {
				const updateData: UpdateVehicleData = {
					cargoIdentification: formData.cargoIdentification.trim(),
					cargoTypeId,
					selectedLengthId: vehicle!.selectedLengthId,
					selectedWeightId: vehicle!.selectedWeightId,
					selectedWidthId: vehicle!.selectedWidthId,
				};
				await updateUserVehicle(vehicle!.id, updateData);
				alertToast('Vehicle updated successfully', 'success', 'Vehicle updated');
			}
			onSuccess();
			onConfirm();
		} catch (error) {
			alertToast(`Failed to ${isCreate ? 'add' : 'update'} vehicle`, 'error', 'Error');
		} finally {
			setIsSaving(false);
		}
	};

	const handleMakeChange = (make: string) => {
		setFormData(prev => ({
			...prev,
			cargoMake: make,
			cargoModel: '',
			cargoTypeId: '',
		}));
		if (errors.cargoMake) {
			setErrors(prev => ({ ...prev, cargoMake: undefined }));
		}
	};

	const handleModelChange = (model: string) => {
		const match = cargoTypes.find(x => x.cargoMake.trim() === formData.cargoMake && x.cargoModel === model);
		if (match && isCreate) {
			// Pre-fill the exact weight (only when creating — editing would change every booking that
			// uses this vehicle). Prefer the vehicle type's assigned exact weight; otherwise fall back to
			// its minimum weight band. Always expressed in the app's configured display unit.
			const unit = appConfigStore.weightDisplayUnit;
			const minWeightKg = getMeasurementValueFromId(match.minimumWeightId);
			let prefillExactWeight: number | undefined;
			if (match.exactWeight != null) {
				prefillExactWeight = convertWeight(
					Number(match.exactWeight),
					parseWeightUnit(match.exactWeightUnit, unit),
					unit,
				);
			} else if (minWeightKg > 0) {
				prefillExactWeight = convertWeight(minWeightKg, 'kg', unit);
			}
			// The vehicle type's minimum length is a floor, not a choice — it may well be one the
			// operator's rules do not offer this customer (a length reserved for a customer type they
			// are not). Settle it through the same filter the picker uses, so the field can never be
			// pre-filled with a value that is not in its own dropdown. Never below the floor: the
			// replacement is the smallest allowed length that still fits the vehicle, and when nothing
			// allowed clears it the type's own minimum stands.
			const prefillLengthId = match.minimumLengthId ?? formData.selectedLengthId;
			const allowedLengthId =
				resolveAllowedMeasurementId(
					prefillLengthId,
					findAllMeasurementsForFerryTrip('LENGTH', 'vehicle'),
					buildFilterContext(match.id),
					getMeasurementValueFromId(match.minimumLengthId),
				) ?? prefillLengthId;

			setFormData(prev => ({
				...prev,
				cargoModel: model,
				cargoTypeId: match.id,
				selectedLengthId: allowedLengthId ?? prev.selectedLengthId,
				selectedWeightId: match.minimumWeightId ?? prev.selectedWeightId,
				selectedWidthId: match.minimumWidthId ?? prev.selectedWidthId,
				exactWeight: prefillExactWeight ?? prev.exactWeight,
				exactWeightUnit: unit,
			}));
			setErrors(prev => ({
				...prev,
				selectedLengthId: undefined,
				selectedWeightId: undefined,
				exactWeight: undefined,
			}));
			setLengthWarning(null);
		} else if (match && !isCreate) {
			const currentLengthValue = getMeasurementValueFromId(vehicle!.selectedLengthId);
			const newMinLengthValue = getMeasurementValueFromId(match.minimumLengthId);

			setLengthWarning(
				newMinLengthValue > currentLengthValue
					? 'This vehicle type requires a longer length than ' +
							'currently set. Please add a new vehicle instead.'
					: null,
			);

			setFormData(prev => ({ ...prev, cargoModel: model, cargoTypeId: match.id }));
		} else {
			setFormData(prev => ({ ...prev, cargoModel: model }));
		}
		if (errors.cargoModel) {
			setErrors(prev => ({ ...prev, cargoModel: undefined }));
		}
	};

	const title = isCreate ? 'Add vehicle' : 'Edit vehicle';

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>{title}</NewModal.Title>
				<Button
					className="new-modal--close"
					icon={{ icon: 'close', iconPos: 'icon-left' }}
					display={Display.Text}
					colors={Colors.Alternate}
					onClick={onCancel}
					labelVisible={false}
				/>
			</NewModal.Header>
			<NewModal.Body>
				<div className="vehicle-edit-modal__form">
					{!isCreate && (
						<MessageBox status="warning" slim>
							{'Updating the make or model will apply to all bookings that have this vehicle assigned.'}
						</MessageBox>
					)}
					{lengthWarning && (
						<MessageBox status="error" slim>
							{lengthWarning}
						</MessageBox>
					)}
					<div className="vehicle-edit-modal__row" data-test-id="vehicle-make-model-row">
						<div className="vehicle-edit-modal__input">
							<SelectInput
								label="Make"
								placeholder="Select make"
								options={makeOptions}
								value={makeOptions.find(o => o.value === formData.cargoMake) ?? null}
								onChange={(option: any) => handleMakeChange(option?.value ?? '')}
								isSearchable
								required
								error={errors.cargoMake}
							/>
						</div>
						<div className="vehicle-edit-modal__input">
							<SelectInput
								label="Model"
								placeholder="Select model"
								options={modelOptions}
								value={modelOptions.find(o => o.value === formData.cargoModel) ?? null}
								onChange={(option: any) => handleModelChange(option?.value ?? '')}
								isSearchable
								required
								error={errors.cargoModel}
							/>
						</div>
					</div>
					<div className="vehicle-edit-modal__row" data-test-id="vehicle-length-rego-row">
						<div className="vehicle-edit-modal__input">
							<SelectInput
								label="Length"
								placeholder="Select length"
								options={lengthOptions}
								value={lengthOptions.find(o => o.value === formData.selectedLengthId) ?? null}
								onChange={(option: any) => {
									const newId = option?.value ?? '';
									setFormData(prev => ({ ...prev, selectedLengthId: newId }));
									if (errors.selectedLengthId) {
										setErrors({ ...errors, selectedLengthId: undefined });
									}
								}}
								isSearchable={false}
								isDisabled={!isCreate}
								tooltip={
									!isCreate
										? 'Length cannot be changed as existing bookings might already use this vehicle'
										: undefined
								}
								error={errors.selectedLengthId}
								required={isCreate}
							/>
						</div>
						<div className="vehicle-edit-modal__input">
							<TextInput
								label={getPascalCaseMessageByKey('vehicle-tab__rego__label', 'Rego')}
								placeholder={`Select ${getLowerCaseMessageByKey('vehicle-tab__rego__label', 'rego')}`}
								value={formData.cargoIdentification}
								onChange={e => {
									const next = e.target.value;
									setFormData(prev => ({ ...prev, cargoIdentification: next }));
									if (errors.cargoIdentification) {
										setErrors({ ...errors, cargoIdentification: undefined });
									}
								}}
								error={errors.cargoIdentification}
								required
							/>
						</div>
					</div>
					{requiresExactWeight && (
						<div className="vehicle-edit-modal__row" data-test-id="vehicle-exact-weight-row">
							<div className="vehicle-edit-modal__input">
								<MultiPartInput
									label="Exact weight"
									required
									parts={[
										{
											key: 'weight',
											type: 'text',
											props: {
												type: 'number',
												min: 0,
												step: 'any',
												placeholder: 'e.g. 1500',
												value: formData.exactWeight ?? '',
												onChange: (e: any) => {
													const raw = e.target.value;
													const parsed = raw === '' ? null : Number(raw);
													setFormData(prev => ({ ...prev, exactWeight: parsed }));
													if (errors.exactWeight) {
														setErrors(prev => ({ ...prev, exactWeight: undefined }));
													}
												},
											},
										},
										{
											key: 'unit',
											type: 'select',
											width: 'w-28',
											props: {
												options: weightUnitOptions,
												value: exactWeightUnitOption,
												onChange: (option: any) =>
													setFormData(prev => ({
														...prev,
														exactWeightUnit: option?.value ?? displayUnit,
													})),
												isSearchable: false,
											},
										},
									]}
								/>
								{errors.exactWeight && (
									<span className="mt-0.5 text-xs text-red-500">{errors.exactWeight}</span>
								)}
							</div>
						</div>
					)}
					{!isCreate && showWeight && (
						<div className="vehicle-edit-modal__row" data-test-id="vehicle-weight-row">
							<div className="vehicle-edit-modal__input">
								<TextInput
									label="Weight"
									value={weightDisplay}
									onChange={() => undefined}
									disabled
									readOnly
									tooltip="Weight reflects the customer's declared weight and can't be edited here."
								/>
							</div>
						</div>
					)}
				</div>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton
					onClick={onCancel}
					display={Display.Outline}
					colors={Colors.Grey}
					disabled={isSaving}
				>
					Cancel
				</NewModal.CancelButton>
				<NewModal.ConfirmButton
					onClick={handleSave}
					display={Display.Solid}
					colors={Colors.Secondary}
					disabled={isSaving || !!lengthWarning}
				>
					{isSaving ? 'Saving...' : 'Save'}
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}

export function openVehicleEditModal(vehicle: UserVehicleDto, onSuccess: () => void): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		const onConfirm = () => {
			store.modal.hide();
			resolve(true);
		};

		const onCancel = () => {
			store.modal.hide();
			reject();
		};

		const modalContent = (
			<VehicleEditModalContent
				vehicle={vehicle}
				onConfirm={onConfirm}
				onCancel={onCancel}
				onSuccess={onSuccess}
			/>
		);

		store.modal?.show('Edit Vehicle', modalContent, {
			className: 'vehicle-edit-modal overflow',
			shouldCloseOnEsc: true,
			shouldCloseOnOverlayClick: false,
			onRequestClose: onCancel,
			newModal: true,
		});
	});
}

export function openVehicleCreateModal(userId: string, onSuccess: () => void): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		const onConfirm = () => {
			store.modal.hide();
			resolve(true);
		};

		const onCancel = () => {
			store.modal.hide();
			reject();
		};

		const modalContent = (
			<VehicleEditModalContent userId={userId} onConfirm={onConfirm} onCancel={onCancel} onSuccess={onSuccess} />
		);

		store.modal?.show('Add Vehicle', modalContent, {
			className: 'vehicle-edit-modal overflow',
			shouldCloseOnEsc: true,
			shouldCloseOnOverlayClick: false,
			onRequestClose: onCancel,
			newModal: true,
		});
	});
}
